import * as Sentry from '@sentry/react';
import { OrderDetail } from '@interfaces/OrderTypes';
import { appStore } from '@store/index';
import { printJobSignature } from '@store/slices/electronPrintSettings';
import { getPlatform } from '@utils/analytics/platform';
import { computeBreakdownResidual, printedOrderTotal } from '@utils/print/orderTotals';
import { ModuleName, PRINT_AUTO_OPTIONS_MAP } from '@utils/constants';

// Observabilidad unificada del flujo de impresión (web + Electron + multi-sucursal).
//
// Objetivo: que cada falla de impresión llegue a Sentry con CONTEXTO suficiente para
// triar sin depender de capturas de pantalla del usuario. Antes, la falla más común
// (impresora no seleccionada) solo mostraba un toast y no se reportaba; y los eventos
// que sí se reportaban iban sin plataforma, versión del shell, comercio, rol ni estado
// multi-sucursal.

// Tipos de incidente de impresión. Se usan como tag `print_error` y como fingerprint
// para que Sentry agrupe por causa y no por stack.
export type PrintErrorTag =
  | 'printer_not_selected' // electronPrinterName nulo al imprimir (no hay impresora elegida en el equipo)
  | 'getPrinters_no_handler' // el shell Electron no expone el IPC getPrinters (shell desactualizado)
  | 'print_ipc_failure' // falló el IPC 'print' (impresión Electron)
  | 'web_print_failure' // falló la generación/impresión en navegador (no Electron)
  | 'web_ble_print_failure' // falló la impresión por Web Bluetooth (build/escritura del ticket)
  | 'web_ble_not_supported' // navigator.bluetooth ausente al intentar imprimir/emparejar por BLE
  | 'web_ble_printer_not_paired' // BLE activo pero sin dispositivo emparejado/reconectable
  | 'web_ble_reconnect_prompt_failure' // falló el chooser filtrado de reconexión asistida (con gesto)
  | 'web_ble_reconnect_silent_failure'; // el GATT rechazó en la reconexión SIN chooser (impresora apagada/fuera de rango)

export type PrintTrigger = 'manual' | 'auto' | 'self_service' | 'init' | 'web' | 'web_ble';

type CapturePrintErrorOptions = {
  tag: PrintErrorTag;
  trigger?: PrintTrigger;
  extra?: Record<string, unknown>;
};

const hasModuleFromStore = (moduleId: (typeof ModuleName)[keyof typeof ModuleName]): boolean => {
  const modules = appStore.getState().subscription?.modulesAndPermissions ?? [];
  return modules.some((m) => m.id === moduleId);
};

const readActiveCommerceId = (): string | null => {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem('id_commerce') : null;
  } catch {
    return null;
  }
};

// Contexto que se adjunta a TODO evento de impresión. Síncrono: todo sale de Redux,
// localStorage y el bridge de plataforma (la versión del shell ya vive en el comercio
// vía useInitElectronVersion → no requiere IPC).
export const buildPrintContext = (): Record<string, unknown> => {
  const state = appStore.getState();
  const commerce = state.commerce;
  const subscription = state.subscription;
  const printSettings = state.electronPrintSettings ?? {};
  const printers = state.electronPrinters ?? [];
  const webBlePrinters = state.webBlePrinters ?? {};

  const activeCommerceId = readActiveCommerceId();
  const homeCommerceId = subscription?.homeCommerce?.id_comercio ?? null;
  const printerKeys = Object.keys(printSettings);
  const printerSelected = printerKeys.some((key) => Boolean(printSettings[key]?.electronPrinterName));

  // Diagnóstico del bug de doble impresión: cuántas entradas de config tiene el equipo, cuántas
  // comparten impresora física, y cuántas con auto-print activo producirían papel IDÉNTICO
  // (firma repetida). `auto_print_duplicate_jobs > 0` confirma el root cause —entradas que el
  // selector colapsa— en el equipo del comerciante sin necesitar acceso a su localStorage.
  const nameGroups = new Map<string, number>();
  printerKeys.forEach((key) => {
    const name = printSettings[key]?.electronPrinterName;
    if (name) nameGroups.set(name, (nameGroups.get(name) ?? 0) + 1);
  });
  const printersSharingName = [...nameGroups.values()].filter((n) => n > 1).reduce((acc, n) => acc + n, 0);

  const autoPrintSignatures = new Set<string>();
  let autoPrintDuplicateJobs = 0;
  printerKeys.forEach((key) => {
    const s = printSettings[key];
    if (!s || s.electronEnabled === false || !s.electronPrinterName) return;
    if (s.electronAutoPrint === PRINT_AUTO_OPTIONS_MAP.disabled.key) return;
    const sig = printJobSignature(s);
    if (autoPrintSignatures.has(sig)) autoPrintDuplicateJobs += 1;
    else autoPrintSignatures.add(sig);
  });

  // Estado Web Bluetooth: síncrono desde Redux (sin acceso a navigator, que rompería
  // jsdom y no aporta contexto útil sin gesto de usuario).
  const webBleKeys = Object.keys(webBlePrinters);
  const webBleEnabled = webBleKeys.some((key) => webBlePrinters[key]?.enabled);
  const webBlePaired = webBleKeys.some((key) => webBlePrinters[key]?.enabled && Boolean(webBlePrinters[key]?.deviceId));

  return {
    platform: getPlatform(),
    desktop_app_version: commerce?.electron_app_version ?? null,
    commerce_id: commerce?.id || null,
    commerce_id_path: commerce?.id_path || null,
    account_id: subscription?.accountId ?? null,
    is_root: Boolean(subscription?.isRoot),
    has_auto_print: hasModuleFromStore(ModuleName.IMPRESION_AUTOMATICA),
    has_multisucursal: hasModuleFromStore(ModuleName.PEDIDOS_MULTISUCURSAL),
    in_foreign_branch: Boolean(activeCommerceId && homeCommerceId && activeCommerceId !== homeCommerceId),
    printer_selected: printerSelected,
    printers_enumerated: printers.length,
    web_ble_enabled: webBleEnabled,
    web_ble_printers_count: webBleKeys.length,
    web_ble_paired: webBlePaired,
    printers_config_count: printerKeys.length,
    printers_sharing_name: printersSharingName,
    auto_print_duplicate_jobs: autoPrintDuplicateJobs,
  };
};

// Tags GLOBALES de Sentry para el framework de rollout. Van junto a `platform` (que el
// flujo de impresión ya adjunta) para poder medir el delta de errores treatment/control.
// Acepta cualquier tag `rollout_*`: las dimensiones NAMESPACED por feature
// (rollout_<key>_variant/state) conviven con las legacy de menu-v2 (rollout_feature/variant/
// state) sin pisarse — son tags distintos en Sentry. Síncrono y tolerante: si Sentry no está
// inicializado (no-prod), no rompe.
export const setFeatureVersionTags = (tags: Record<string, string | null | undefined>): void => {
  try {
    Object.entries(tags).forEach(([key, value]) => {
      if (value) Sentry.setTag(key, String(value));
    });
  } catch {
    // off — la observabilidad no debe romper la app
  }
};

// Error de una SUPERFICIE de rollout que vive en CONTROL (p.ej. el coachmark de invitación).
// Va DIRECTO a Sentry — NUNCA como behavior='error': ese behavior alimenta el guardrail
// max_error_rate_pct de la cohorte TREATMENT y un crash de la superficie de invitación no es
// una regresión de esa cohorte (la contaminaría). Best-effort: jamás lanza.
export const captureRolloutSurfaceError = (error: unknown, surface: string): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setTag('rollout_surface', surface);
      Sentry.captureException(error);
    });
  } catch {
    // off — la observabilidad no debe romper la app
  }
};

// Reporta una DIVERGENCIA de rollout a Sentry: el FE mostró/escribió una variante optimista que
// el backend rechazó (REQUIRES_ROOT) o cuyo PUT se agotó tras MAX_ATTEMPTS. Es la materia prima de
// la alerta "treatment en pantalla sin switch en BD". NO-PII: solo IDs de feature/comercio y la
// causa. Síncrono y tolerante: si Sentry está apagado (no-prod) no rompe.
export const captureRolloutDivergence = (info: {
  feature_key: string;
  commerce_id: string | null;
  to_variant: string | null;
  from_variant: string | null;
  trigger: string | null;
  attempts: number;
  cause: 'rejected_terminal' | 'exhausted';
}): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('rollout_divergence', info.cause);
      scope.setTag('rollout_feature', info.feature_key);
      // Agrupa por feature + causa (no por stack): una alerta por modo de fuga, no ruido.
      scope.setFingerprint(['rollout-divergence', info.feature_key, info.cause]);
      scope.setContext('rollout_divergence', {
        feature_key: info.feature_key,
        commerce_id: info.commerce_id,
        to_variant: info.to_variant,
        from_variant: info.from_variant,
        trigger: info.trigger,
        attempts: info.attempts,
        cause: info.cause,
      });
      Sentry.captureMessage(`rollout switch perdido (${info.cause}) — optimista revertido`, 'warning');
    });
  } catch {
    // tolerante: la observabilidad no debe romper el drain del outbox.
  }
};

// Avisa a Sentry que un ticket se imprimió con el desglose DESCUADRADO: `pedido.total` (lo que
// se cobró de verdad, y lo que el papel imprime como "Total a pagar") no coincide con la suma
// de las líneas del desglose. Es la señal de que hay pedidos con datos inconsistentes en
// origen — hoy, propina cobrada y guardada en 0. NO es un error de impresión: va como
// `warning` y NO entra en el union `PrintErrorTag`, para no contaminar la tasa de fallos de
// impresión. Sin PII: solo IDs, ruta y montos. Síncrono y tolerante: jamás lanza.
export const captureTicketAmountsResidual = (info: {
  order_id: string | null;
  route: 'web_ble' | 'electron' | 'pdf';
  residual: number;
  printed_total: number;
  include_shipping: boolean;
}): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('ticket_amounts_residual', info.residual > 0 ? 'under' : 'over');
      scope.setTag('print_route', info.route);
      // Agrupa por ruta y sentido del descuadre, no por stack: una alerta por modo de fuga.
      scope.setFingerprint(['printing', 'ticket_amounts_residual', info.route]);
      scope.setContext('printing', { ...buildPrintContext(), ...info });
      Sentry.captureMessage(`ticket con desglose descuadrado (${info.route})`, 'warning');
    });
  } catch {
    // off — la observabilidad no debe romper la impresión
  }
};

// Rutas de impresión que emiten un ticket completo.
export type PrintRoute = 'web_ble' | 'electron' | 'pdf';

// ¿Esta ruta imprime la línea "Costo de envío" para este pedido? Espejo EXACTO de la condición
// de cada builder: webble (`ticketModel.pushFooterTotals`) y PDF (`printUtils.createTotalsSection`)
// la imprimen solo en `delivery`; el ticket Electron (`electronTicket/getOrderFooter`) la imprime
// SIEMPRE. El residuo tiene que espejear las líneas del papel, así que aquí se replica el gate.
const printsShippingLine = (route: PrintRoute, deliveryMethod: string | undefined): boolean =>
  route === 'electron' || deliveryMethod === 'delivery';

// Calcula el residuo del desglose de un ticket y, si no cuadra, lo reporta. Es el ÚNICO punto
// desde el que se llama a Sentry por este motivo: vive en el borde (los hooks de impresión) y
// no dentro de los builders, que son puros y corren en tests de entorno `node` donde importar
// el store rompería.
export const reportTicketResidual = (order: OrderDetail | undefined | null, route: PrintRoute): void => {
  try {
    const pedido = order?.pedido;
    if (!pedido) return;
    const residual = computeBreakdownResidual(pedido, {
      includeShipping: printsShippingLine(route, pedido.metodo_entrega),
    });
    if (residual === 0) return;
    captureTicketAmountsResidual({
      order_id: pedido.id ?? null,
      route,
      residual,
      printed_total: printedOrderTotal(pedido),
      include_shipping: printsShippingLine(route, pedido.metodo_entrega),
    });
  } catch {
    // off — la observabilidad no debe romper la impresión
  }
};

// Reporta una falla de impresión a Sentry con tag, fingerprint y contexto completo.
export const capturePrintError = (error: unknown, options: CapturePrintErrorOptions): void => {
  const context = { ...buildPrintContext(), ...(options.extra ?? {}) };
  Sentry.withScope((scope) => {
    scope.setTag('print_error', options.tag);
    scope.setTag('print_platform', String(context.platform));
    if (options.trigger) scope.setTag('print_trigger', options.trigger);
    scope.setFingerprint(['printing', options.tag]);
    scope.setContext('printing', context);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
};
